import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useLocation } from 'react-router-dom';
import LanguageSwitcher from '@/components/feature/LanguageSwitcher';
import { categories } from '@/mocks/categories';

const navLinks: { key: string; to?: string; href?: string }[] = [
  { key: 'nav.home', to: '/' },
  { key: 'nav.spreadsheet', to: '/spreadsheet' },
  { key: 'nav.guides', to: '/guides' },
  { key: 'nav.shoppingGuide', to: '/article/allchinabuy-buying-guide' },
  { key: 'nav.reviews', to: '/reviews' },
  { key: 'nav.safety', to: '/safety' },
];

export default function Header() {
  const { t } = useTranslation();
  const [mobileOpen, setMobileOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    setMobileOpen(false);
  }, [location]);

  return (
    <header className="fixed top-0 left-0 right-0 z-50 bg-background-50/95 backdrop-blur border-b border-background-200">
      <div className="mx-auto max-w-7xl px-4 md:px-6">
        <div className="flex h-16 items-center justify-between gap-3">
          <Link to="/" className="flex items-center gap-2 cursor-pointer shrink-0">
            <span className="flex w-9 h-9 items-center justify-center rounded-lg bg-primary-500 text-background-50">
              <i className="ri-table-line text-lg"></i>
            </span>
            <span className="font-heading text-base md:text-lg font-bold whitespace-nowrap text-foreground-950">
              AllChinaBuy <span className="text-accent-500">Spreadsheet</span>
            </span>
          </Link>

          <nav className="hidden lg:flex items-center gap-1">
            {navLinks.map((link) =>
              link.to ? (
                <Link
                  key={link.key}
                  to={link.to}
                  className="px-3 py-2 text-sm font-medium rounded-full text-foreground-700 hover:bg-background-100 hover:text-foreground-950 transition-colors whitespace-nowrap cursor-pointer"
                >
                  {t(link.key)}
                </Link>
              ) : (
                <a
                  key={link.key}
                  href={link.href}
                  className="px-3 py-2 text-sm font-medium rounded-full text-foreground-700 hover:bg-background-100 hover:text-foreground-950 transition-colors whitespace-nowrap cursor-pointer"
                >
                  {t(link.key)}
                </a>
              )
            )}
          </nav>

          <div className="flex items-center gap-2">
            <LanguageSwitcher />
            <button
              onClick={() => setMobileOpen((o) => !o)}
              className="lg:hidden flex w-10 h-10 items-center justify-center rounded-full text-foreground-900 cursor-pointer"
              aria-label="Toggle menu"
            >
              <i className={`${mobileOpen ? 'ri-close-line' : 'ri-menu-line'} text-xl`}></i>
            </button>
          </div>
        </div>
      </div>

      {mobileOpen && (
        <div className="lg:hidden bg-background-50 border-t border-background-200 px-4 py-4 max-h-[80vh] overflow-y-auto">
          <nav className="space-y-1">
            {navLinks.map((link) =>
              link.to ? (
                <Link
                  key={link.key}
                  to={link.to}
                  className="block px-3 py-2.5 text-sm font-medium text-foreground-800 rounded-lg hover:bg-background-100 whitespace-nowrap"
                >
                  {t(link.key)}
                </Link>
              ) : (
                <a
                  key={link.key}
                  href={link.href}
                  className="block px-3 py-2.5 text-sm font-medium text-foreground-800 rounded-lg hover:bg-background-100 whitespace-nowrap"
                >
                  {t(link.key)}
                </a>
              )
            )}
          </nav>

          <div className="mt-4 pt-4 border-t border-background-200">
            <p className="px-3 text-xs font-semibold uppercase tracking-wide text-foreground-500">
              {t('nav.categories')}
            </p>
            <div className="mt-2 grid grid-cols-2 gap-1">
              {categories.map((cat) => (
                <a
                  key={cat.key}
                  href={cat.url}
                  target="_blank"
                  rel="noopener nofollow"
                  className="flex items-center gap-2 px-3 py-2 text-sm text-foreground-700 rounded-lg hover:bg-primary-50 hover:text-primary-700 transition-colors cursor-pointer"
                >
                  <span className="flex w-5 h-5 items-center justify-center text-primary-600 shrink-0">
                    <i className={`${cat.icon} text-sm`}></i>
                  </span>
                  <span className="whitespace-nowrap overflow-hidden text-ellipsis">{t(`cat.${cat.key}`)}</span>
                </a>
              ))}
            </div>
          </div>
        </div>
      )}
    </header>
  );
}